import { Node as TiptapNode } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

interface Entry { id: string; level: number; text: string; pos: number }
type Labels = Record<string, string>;
function readLabels(raw: string | null): Labels { try { const value = JSON.parse(raw || '{}'); return value && typeof value === 'object' && !Array.isArray(value) ? value : {}; } catch { return {}; } }
function entries(doc: PMNode): Entry[] {
    const out: Entry[] = [];
    doc.descendants((node, pos) => {
        if (node.type.name === 'documentToc') return false;
        if (node.type.name === 'heading' && node.attrs.level <= 3) out.push({ id: node.attrs.id || '', level: node.attrs.level, text: node.textContent, pos });
    });
    return out;
}
function strings(language: string) {
    return language === 'fr' ? { title: 'Table des matières', empty: 'Utilisez les titres 1, 2 ou 3 pour remplir cette table.', edit: 'Modifier le libellé (vide = titre automatique)', go: 'Aller au titre' }
        : { title: 'Table of contents', empty: 'Use Heading 1, 2 or 3 to populate this table.', edit: 'Edit label (empty = automatic heading)', go: 'Go to heading' };
}
function titleFor(title: string, language: string) {
    return !title || ['Table of contents', 'Table des matières'].includes(title) ? strings(language).title : title;
}
export const DocumentToc = TiptapNode.create<{ getLanguage: () => string }>({
    name: 'documentToc', group: 'block', atom: true, selectable: true,
    addOptions() { return { getLanguage: () => 'en' }; },
    addAttributes() { return {
        title: { default: 'Table of contents', parseHTML: el => el.getAttribute('data-title'), renderHTML: attrs => ({ 'data-title': attrs.title }) },
        empty: { default: '', parseHTML: el => el.getAttribute('data-empty'), renderHTML: attrs => ({ 'data-empty': attrs.empty }) },
        labels: { default: {}, parseHTML: el => readLabels(el.getAttribute('data-labels')), renderHTML: attrs => ({ 'data-labels': JSON.stringify(attrs.labels) }) },
    }; },
    parseHTML() { return [{ tag: 'div[data-document-toc]' }]; },
    renderHTML({ HTMLAttributes }) { return ['div', { ...HTMLAttributes, 'data-document-toc': '' }]; },
    addNodeView() {
        const getLanguage = this.options.getLanguage;
        return ({ node, editor, getPos }) => {
            let current = node;
            let signature = '';
            const dom = document.createElement('div');
            dom.dataset.documentToc = ''; dom.contentEditable = 'false';
            const refresh = () => {
                // Do not overwrite an in-progress custom label on a focus or
                // remote transaction. Commit merges into the latest node attrs.
                if (dom.contains(document.activeElement)) return;
                const items = entries(editor.state.doc);
                const language = getLanguage();
                const next = JSON.stringify([items, current.attrs, language, editor.isEditable]);
                if (next === signature) return;
                signature = next;
                dom.replaceChildren();
                const title = document.createElement('strong');
                title.textContent = titleFor(current.attrs.title, language); dom.append(title);
                if (!items.length) { const p = document.createElement('p'); p.textContent = strings(language).empty; dom.append(p); }
                for (const entry of items) {
                    const row = document.createElement('p'); row.dataset.headingId = entry.id;
                    row.style.paddingLeft = `${(entry.level - 1) * 16}px`;
                    const label = document.createElement('span');
                    label.textContent = current.attrs.labels[entry.id] ?? entry.text ?? '…';
                    label.contentEditable = String(editor.isEditable && !!entry.id);
                    label.setAttribute('role', 'textbox'); label.setAttribute('aria-label', strings(language).edit);
                    label.onkeydown = e => { if (e.key === 'Enter') { e.preventDefault(); label.blur(); } if (e.key === 'Escape') { label.textContent = current.attrs.labels[entry.id] ?? entry.text; label.blur(); } };
                    label.onblur = () => {
                        const pos = getPos();
                        if (!editor.isEditable || typeof pos !== 'number') return;
                        const live = editor.state.doc.nodeAt(pos);
                        if (live?.type.name !== 'documentToc') return;
                        const labels = { ...live.attrs.labels };
                        const value = label.textContent?.trim() || '';
                        if (value) labels[entry.id] = value; else delete labels[entry.id];
                        if (JSON.stringify(labels) !== JSON.stringify(live.attrs.labels)) editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...live.attrs, labels }));
                        signature = ''; queueMicrotask(refresh);
                    };
                    const link = document.createElement('button'); link.type = 'button'; link.textContent = ' ↗'; link.title = strings(language).go;
                    link.onclick = () => { const found = entries(editor.state.doc).find(item => item.id === entry.id); if (found) (editor.view.nodeDOM(found.pos) as HTMLElement)?.scrollIntoView({ block: 'start', behavior: 'smooth' }); };
                    row.append(label, link); dom.append(row);
                }
            };
            refresh(); editor.on('transaction', refresh);
            return { dom, stopEvent: event => event.target instanceof HTMLElement && dom.contains(event.target), ignoreMutation: () => true,
                update: updated => { if (updated.type !== current.type) return false; current = updated; refresh(); return true; },
                destroy: () => editor.off('transaction', refresh) };
        };
    },
});
export function materializeDocumentToc(html: string, language = 'en'): string {
    const root = new DOMParser().parseFromString(html, 'text/html').body;
    const seen = new Set<string>();
    const items = Array.from(root.querySelectorAll('h1,h2,h3')).map((el, index) => {
        if (!el.id || seen.has(el.id)) el.id = `export-heading-${index}`;
        seen.add(el.id);
        return { id: el.id, level: Number(el.tagName[1]), text: el.textContent || '' };
    });
    root.querySelectorAll<HTMLElement>('[data-document-toc]').forEach(el => {
        const labels = readLabels(el.dataset.labels || null);
        const title = titleFor(el.dataset.title || '', language);
        el.replaceChildren();
        const p = document.createElement('p'); const strong = document.createElement('strong'); strong.textContent = title; p.append(strong); el.append(p);
        if (!items.length) { const empty = document.createElement('p'); empty.textContent = strings(language).empty; el.append(empty); }
        for (const entry of items) {
            const p = document.createElement('p'); p.dataset.tocLevel = String(entry.level); p.style.paddingLeft = `${(entry.level - 1) * 16}px`;
            const link = document.createElement('a'); link.setAttribute('href', `#${entry.id}`); link.dataset.tocTarget = entry.id;
            link.textContent = labels[entry.id] ?? entry.text;
            p.append(link); el.append(p);
        }
    });
    return root.innerHTML;
}
